import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';

import { CHART_INK, CHART_SEMANTIC } from '../constants/chartPalette';
import {
  ChartDataPoint, colourForToken, formatValue, WidgetNothingToDraw, WidgetPeriodChip,
} from '../shared';

/** Arc geometry. Width and stroke are fixed; the arc is derived from both. */
const ARC_WIDTH = 132;
const ARC_STROKE = 12;
/** The threshold ring: thin, outside the value arc, and only a reference. */
const BAND_STROKE = 3;
const BAND_GAP = 3;

/** What a threshold band is called when the author did not name it. */
const BAND_NAME = Object.freeze({
  DANGER: 'critical',
  WARNING: 'warning',
  SUCCESS: 'good',
  INFO: 'info',
  NEUTRAL: 'neutral',
});

/**
 * `display.thresholds` → ranges over the gauge's own scale.
 *
 * The author writes rules (`LT 4`, `BETWEEN 4 and 4.5`, `GTE 4.5`); the arc
 * needs spans. An open-ended rule is closed against the gauge's `min` or
 * `max`, which is the only honest reading — a rule saying "below 4 is
 * critical" on a 0–5 gauge means the band starts at 0, not at negative
 * infinity.
 *
 * Rules that fall outside the scale are dropped rather than clamped into a
 * zero-width sliver: a band the reader cannot see is worse than no band,
 * because the legend would still list it.
 */
const bandsFrom = (thresholds, min, max) => (thresholds ?? [])
  .map((rule) => {
    const { operator, value, value_to: valueTo } = rule;
    let from = min;
    let to = max;
    if (operator === 'LT' || operator === 'LTE') to = value;
    else if (operator === 'GT' || operator === 'GTE') from = value;
    else if (operator === 'BETWEEN') { from = value; to = valueTo ?? max; }
    else return null;
    return {
      from: Math.max(min, from),
      to: Math.min(max, to),
      token: rule.color_token,
      label: rule.label ?? null,
      operator,
      value,
      valueTo,
    };
  })
  .filter((band) => band && band.to > band.from)
  .sort((a, b) => a.from - b.from);

/** `< 4`, `4 – 4.5`, `≥ 4.5` — the rule as the author would read it back. */
const bandRange = (band, format) => {
  const value = formatValue(band.value, format);
  if (band.operator === 'LT') return `< ${value}`;
  if (band.operator === 'LTE') return `≤ ${value}`;
  if (band.operator === 'GT') return `> ${value}`;
  if (band.operator === 'GTE') return `≥ ${value}`;
  return `${value} – ${formatValue(band.valueTo, format)}`;
};

/**
 * Monitoring against a target.
 *
 * A half-ring that sweeps rather than appears: the arc animates its
 * `stroke-dashoffset` from the full length to zero, so the value visibly fills
 * up. On a widget whose whole job is "are we there yet", the fill *is* the
 * reading.
 *
 * ## What the arc has to say, and where each part comes from
 *
 * - **The fill** is `ratio` when the contract sends it. The arc used to derive
 *   `value / max`, which is only right when the scale starts at zero —
 *   `INDICATOR` publishes a `min`, and a gauge from 60 to 100 showing 70 is a
 *   quarter full, not seven tenths.
 * - **The colour** is `color_token`, the answer to the author's thresholds
 *   evaluated server-side. A gauge recomputing it would disagree with the KPI
 *   beside it the first time either rule changed. `warnAt` survives as the
 *   fallback for a payload with no token, which is every v1 one.
 * - **`min` and `max` are printed at the ends.** Without them the sweep is
 *   unreadable: a needle at four fifths says nothing until you know whether
 *   the scale ends at 5 or at 500, and both numbers are in the payload.
 * - **`target` is a notch on the arc**, where it falls, rather than a
 *   sentence underneath. Showing the gap between where you are and where you
 *   should be — at a glance, without arithmetic — is the entire argument for
 *   drawing a gauge instead of printing a number.
 * - **`thresholds` are a thin band ring** outside the value arc, with a
 *   legend. The token says which band you are *in*; the ring says what the
 *   bands *are*, which is the part a reader cannot infer from one colour.
 *   They live in `display`, so the caller passes them.
 *
 * Nothing here is invented. An earlier version printed "on track" beside the
 * target, a verdict the contract never gave and the author never configured.
 *
 * @param {object} props
 * @param {object} props.data `INDICATOR`'s own `data` — see propTypes.
 * @param {object[]} [props.thresholds] `display.thresholds`, for the band
 *   ring. Also read off `data.thresholds`: widgets are handed `data` and not
 *   `display` today, so a caller with nowhere else to put them can thread
 *   them through the payload. That fallback goes when `display` arrives.
 * @param {number} [props.warnAt] Ratio of target below which the arc turns red,
 *   used only when the payload carries no `color_token`.
 * @param {string} [props.className]
 */
function GaugeChartWidget({ data, thresholds, warnAt = 0.75, className = '' }) {
  const {
    value, max: rawMax, min: rawMin, target, format, color_token: colorToken, period,
    unit = '', caption,
  } = data ?? {};

  /*
    `min`/`max` are optional slots on `INDICATOR`, and most authors leave them
    empty — the engine then sends neither. Defaulting to 0–100 pinned the
    needle at the top for every reading above 100: an average HTTP status of
    500 drew a full arc and said nothing (CT-5883).

    So only fall back when the payload is silent, and derive a scale that
    actually contains what is being drawn: zero up to the value and the
    target, rounded out so the needle does not sit flush against the end.
  */
  const [min, max] = useMemo(() => {
    const hasMin = Number.isFinite(rawMin);
    const hasMax = Number.isFinite(rawMax);
    if (hasMin && hasMax) return [rawMin, rawMax];

    const points = [value, target].filter((n) => Number.isFinite(n));
    if (!points.length) return [hasMin ? rawMin : 0, hasMax ? rawMax : 100];

    const low = hasMin ? rawMin : Math.min(0, ...points);
    const peak = Math.max(...points, low);
    // Headroom so a value equal to the top of the scale still reads as a
    // gauge rather than a filled bar; `|| 1` keeps an all-zero reading sane.
    const high = hasMax ? rawMax : low + (Math.abs(peak - low) * 1.25 || 1);
    return [low, high];
  }, [rawMin, rawMax, value, target]);

  const bands = useMemo(
    () => bandsFrom(thresholds ?? data?.thresholds, min, max),
    [thresholds, data, min, max],
  );

  // A gauge with no reading is not a gauge. `INDICATOR` arrives like this
  // from dev whenever its measure is a SUM over null rows: `min`, `max` and
  // `format` present, `value` absent. Returning null left a blank card.
  if (value == null) return <WidgetNothingToDraw />;

  const span = max - min;
  const toRatio = (point) => (span > 0 ? Math.min(1, Math.max(0, (point - min) / span)) : 0);
  const rawRatio = data?.ratio ?? toRatio(value);
  const ratio = Math.min(1, Math.max(0, rawRatio));

  const radius = (ARC_WIDTH - ARC_STROKE) / 2;
  const arcLength = Math.PI * radius;
  const centreY = ARC_WIDTH / 2;
  const centreX = ARC_WIDTH / 2;
  const path = `M ${ARC_STROKE / 2} ${centreY} A ${radius} ${radius} 0 0 1 ${ARC_WIDTH - ARC_STROKE / 2} ${centreY}`;

  const bandRadius = radius + ARC_STROKE / 2 + BAND_GAP;
  const bandLength = Math.PI * bandRadius;
  const bandPath = `M ${centreX - bandRadius} ${centreY} A ${bandRadius} ${bandRadius} 0 0 1 ${centreX + bandRadius} ${centreY}`;

  const met = target == null || value >= target;
  const close = target != null && value >= target * warnAt;
  const derived = met ? CHART_SEMANTIC.ok : close ? CHART_SEMANTIC.warn : CHART_SEMANTIC.bad;
  const stroke = colorToken ? colourForToken(colorToken, derived) : derived;

  /*
    The distance to the target, formatted like every other number on the
    gauge. It used to be `Math.round`ed, which on a decimal scale swallowed
    the very gap it was reporting — 4.32 against a 4.5 target read "0 under",
    i.e. on target. Running it through `format` keeps the three numbers in the
    sentence under one rule, whatever that rule is.
  */
  const gap = target == null ? null : formatValue(Math.abs(target - value), format);
  const shown = formatValue(value, format);
  const shownTarget = target == null ? null : formatValue(target, format);

  /** A point on the value arc, for the target notch. */
  const atRatio = (r, atRadius) => {
    const angle = Math.PI - r * Math.PI;
    return { x: centreX + atRadius * Math.cos(angle), y: centreY - atRadius * Math.sin(angle) };
  };
  const targetRatio = target == null ? null : toRatio(target);
  const notchInner = targetRatio == null ? null : atRatio(targetRatio, radius - ARC_STROKE / 2 - 1);
  const notchOuter = targetRatio == null ? null : atRatio(targetRatio, radius + ARC_STROKE / 2 + 1);

  // Room above the arc for the band ring, and below it for the end labels.
  const height = ARC_WIDTH / 2 + ARC_STROKE + BAND_GAP + BAND_STROKE;

  return (
    // Centred rather than top-aligned: a gauge is one object, and in a slot
    // taller than it needs, hanging from the top reads as a layout mistake.
    <div className={`flex h-full flex-col items-center justify-center gap-0.5 ${className}`.trim()}>
      <ChartDataPoint
        label={
          target == null
            ? `${shown} of ${formatValue(max, format)}`
            : `${shown} against a ${shownTarget} target — ${gap}${unit} ${value >= target ? 'over' : 'under'}`
        }
      >
        <div className="relative" style={{ width: ARC_WIDTH, height }}>
          <svg
            width={ARC_WIDTH}
            height={height}
            viewBox={`${centreX - bandRadius - BAND_STROKE} 0 ${(bandRadius + BAND_STROKE) * 2} ${height}`}
            aria-hidden
          >
            {/* The bands the author configured, as a reference ring. */}
            {bands.map((band) => {
              const from = toRatio(band.from);
              const to = toRatio(band.to);
              return (
                <path
                  key={`${band.operator}-${band.from}`}
                  d={bandPath}
                  fill="none"
                  stroke={colourForToken(band.token, CHART_INK.line)}
                  strokeWidth={BAND_STROKE}
                  strokeDasharray={`${bandLength * (to - from)} ${bandLength}`}
                  strokeDashoffset={-bandLength * from}
                />
              );
            })}

            <path
              d={path}
              fill="none"
              stroke={CHART_INK.line}
              strokeWidth={ARC_STROKE}
              strokeLinecap="round"
            />
            <path
              d={path}
              fill="none"
              stroke={stroke}
              strokeWidth={ARC_STROKE}
              strokeLinecap="round"
              strokeDasharray={`${arcLength * ratio} ${arcLength}`}
              className="wg-enter-sweep"
              style={{ '--wg-sweep-from': `${arcLength * ratio}px` }}
            />

            {/* Where the target falls, cut through the arc. */}
            {notchInner && notchOuter ? (
              <line
                x1={notchInner.x}
                y1={notchInner.y}
                x2={notchOuter.x}
                y2={notchOuter.y}
                stroke={CHART_INK.surface}
                strokeWidth="2.5"
              />
            ) : null}
          </svg>

          <div className="absolute inset-x-0 bottom-0 text-center text-[22px] font-bold leading-none text-content">
            {shown}
          </div>
        </div>
      </ChartDataPoint>

      {/* The ends of the scale, under the ends of the arc. */}
      <div className="flex w-full items-baseline justify-between text-[9px] text-content-faint" style={{ maxWidth: ARC_WIDTH }}>
        <span>{formatValue(min, format)}</span>
        {shownTarget ? <span className="text-content-muted">{`target ${shownTarget}`}</span> : null}
        <span>{formatValue(max, format)}</span>
      </div>

      {caption ? <span className="text-[11px] text-content-muted">{caption}</span> : null}

      {bands.length ? (
        <div className="flex flex-wrap justify-center gap-x-2 gap-y-0.5">
          {bands.map((band) => (
            <span key={`legend-${band.operator}-${band.from}`} className="flex items-center gap-1">
              <span
                aria-hidden
                className="h-1.5 w-1.5 shrink-0 rounded-full"
                style={{ background: colourForToken(band.token, CHART_INK.line) }}
              />
              <span className="text-[9px] text-content-muted">
                {`${bandRange(band, format)} ${band.label ?? BAND_NAME[band.token] ?? ''}`.trim()}
              </span>
            </span>
          ))}
        </div>
      ) : null}

      <WidgetPeriodChip period={period} />
    </div>
  );
}

GaugeChartWidget.propTypes = {
  /** `INDICATOR`'s `data`, as `output.schema.json` publishes it. */
  data: PropTypes.shape({
    value: PropTypes.number.isRequired,
    min: PropTypes.number,
    max: PropTypes.number,
    target: PropTypes.number,
    ratio: PropTypes.number,
    format: PropTypes.object,
    color_token: PropTypes.string,
    period: PropTypes.object,
    /** Not in `data`; see the `thresholds` prop. */
    thresholds: PropTypes.arrayOf(PropTypes.object),
    // Pre-v2 callers; neither is in the contract.
    unit: PropTypes.string,
    caption: PropTypes.string,
  }).isRequired,
  /** `display.thresholds` — `{operator, value, value_to?, color_token, label?}`. */
  thresholds: PropTypes.arrayOf(PropTypes.shape({
    operator: PropTypes.oneOf(['LT', 'LTE', 'GT', 'GTE', 'BETWEEN']),
    value: PropTypes.number,
    value_to: PropTypes.number,
    color_token: PropTypes.string,
    label: PropTypes.string,
  })),
  warnAt: PropTypes.number,
  className: PropTypes.string,
};

export default memo(GaugeChartWidget);
